// ═══════════════════════════════════════════════════════════
// V2 API Client
//
// All GAB requests route through /api/v2proxy (Vite dev middleware
// or Cloudflare Pages Function in production). Auth is handled
// server-side via HTTP-only cookies — the client never stores
// or injects GAB tokens.
// ═══════════════════════════════════════════════════════════

import type { PaginatedResponse, QueryOptions } from './types';
import { getToken } from '../auth/token';

const API_BASE = '/api/v2proxy';
const APP_ID = import.meta.env.VITE_V2_APP_ID;

// ── Auth endpoints ──────────────────────────────────────────
// Tokens live in HttpOnly cookies; these functions talk to the
// server-side auth handlers (Vite plugin in dev, Pages Functions
// in prod) which set/clear the cookies and return user identity.

export async function gabLogin(email: string, password: string): Promise<{ user: import('./types').V2User }> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Login failed' })) as { error?: string };
    throw new Error(body.error ?? 'Login failed');
  }
  return res.json();
}

export async function gabLogout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

export async function volunteerRegister(
  email: string, password: string, firstName: string, lastName: string,
): Promise<{ id: string; email: string; firstName: string; lastName: string; fullName: string }> {
  const res = await fetch('/api/auth/volunteer-register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, firstName, lastName }),
  });
  const body = await res.json() as { user?: { id: string; email: string; firstName: string; lastName: string; fullName: string }; error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Registration failed');
  return body.user!;
}

export async function volunteerLogin(
  email: string, password: string,
): Promise<{ id: string; email: string; firstName: string; lastName: string; fullName: string }> {
  const res = await fetch('/api/auth/volunteer-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json() as { user?: { id: string; email: string; firstName: string; lastName: string; fullName: string }; error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Login failed');
  return body.user!;
}

export async function volunteerForgotPassword(email: string): Promise<void> {
  const res = await fetch('/api/auth/volunteer-forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const body = await res.json() as { error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Request failed');
}

export async function volunteerResetPassword(email: string, code: string, password: string): Promise<void> {
  const res = await fetch('/api/auth/volunteer-reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, password }),
  });
  const body = await res.json() as { error?: string };
  if (!res.ok) throw new Error(body.error ?? 'Reset failed');
}

export async function gabCheckSession(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Core Fetch ──────────────────────────────────────────────
// Cookies are sent automatically via credentials: 'include'.
// The proxy reads gab_token and injects the Authorization header.

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const isMultipart = options.body instanceof FormData;
  const oidcToken = getToken();
  const authHeader = oidcToken ? { 'Authorization': `Bearer ${oidcToken}` } : {};
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: isMultipart
      ? { ...authHeader, ...(options.headers as Record<string, string> ?? {}) }
      : { 'Content-Type': 'application/json', ...authHeader, ...(options.headers as Record<string, string> ?? {}) },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    console.error(`[API ${res.status}] ${path}`, body);
    throw new Error(`API ${res.status}: ${path} — ${body}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ── Table path helper ───────────────────────────────────────

function tablePath(tableKey: string) {
  return `/v2/apps/${APP_ID}/tables/${tableKey}/records`;
}

// ── Response normalizer ─────────────────────────────────────

function normalizeListResponse<T>(raw: unknown): PaginatedResponse<T> {
  const r = raw as Record<string, unknown>;
  const records = (r.records ?? r.items ?? r.data ?? []) as T[];
  return { records, total: (r.total as number) ?? records.length };
}

// ── CRUD Operations ─────────────────────────────────────────

export async function fetchRecords<T = Record<string, unknown>>(
  tableKey: string,
  opts?: { limit?: number; offset?: number; sort?: { field: string; direction: 'asc' | 'desc' }; anonymous?: boolean },
): Promise<PaginatedResponse<T>> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.offset) params.set('offset', String(opts.offset));
  if (opts?.sort) {
    params.set('sort', opts.sort.field);
    params.set('order', opts.sort.direction);
  }
  const qs = params.toString();
  const path = `${tablePath(tableKey)}${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  return normalizeListResponse<T>(raw);
}

export async function queryRecords<T = Record<string, unknown>>(
  tableKey: string,
  query: QueryOptions,
  opts?: { anonymous?: boolean },
): Promise<PaginatedResponse<T>> {
  void opts;

  // Separate nin filters — GAB doesn't support nin server-side; apply client-side after.
  const ninFilters = query.filters?.filter(f => f.operator === 'nin') ?? [];
  const serverFilters = query.filters?.filter(f => f.operator !== 'nin') ?? [];

  // Map operator aliases and normalise value formats for GAB's query endpoint.
  const gabFilters = serverFilters.map(f => {
    let operator = f.operator as string;
    let value = f.value;

    // Legacy alias mapping
    if (operator === 'is_empty') operator = 'is_null';
    if (operator === 'is_not_empty') operator = 'not_null';

    // GAB expects comma-separated strings for multi-value operators
    if ((operator === 'in' || operator === 'nin') && Array.isArray(value)) {
      value = (value as unknown[]).join(',');
    }
    if (operator === 'between' && Array.isArray(value) && (value as unknown[]).length === 2) {
      value = (value as unknown[]).join(',');
    }

    // GAB POST /records/query requires value to be a string (or undefined for null operators)
    const needsValue = operator !== 'is_null' && operator !== 'not_null';
    const stringValue = needsValue && value != null ? String(value) : value;

    return { field: f.field, operator, value: stringValue };
  });

  const body: Record<string, unknown> = {
    limit: query.limit ?? 500,
    offset: query.offset ?? 0,
  };
  if (gabFilters.length > 0) body.filter = gabFilters;
  if (query.sort && query.sort.field !== 'created_at' && query.sort.field !== 'updated_at') {
    body.sort = { field: query.sort.field, direction: query.sort.direction };
  }
  if (query.search) body.search = query.search;

  const raw = await apiFetch<unknown>(`${tablePath(tableKey)}/query`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const result = normalizeListResponse<T>(raw);

  // Client-side nin pass (not supported by GAB server)
  if (ninFilters.length > 0) {
    result.records = result.records.filter(row =>
      ninFilters.every(f => {
        const val = (row as Record<string, unknown>)[f.field];
        const fv = Array.isArray(f.value) ? f.value : [f.value];
        return !(fv as unknown[]).map(String).includes(String(val ?? ''));
      })
    );
    result.total = result.records.length;
  }

  return result;
}

export async function createRecord<T = Record<string, unknown>>(
  tableKey: string,
  data: Record<string, unknown>,
  opts?: { anonymous?: boolean },
): Promise<T> {
  void opts;
  return apiFetch<T>(tablePath(tableKey), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecord<T = Record<string, unknown>>(
  tableKey: string,
  recordId: number,
  data: Record<string, unknown>,
  opts?: { anonymous?: boolean },
): Promise<T> {
  void opts;
  return apiFetch<T>(`${tablePath(tableKey)}/${recordId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function fetchRecord<T = Record<string, unknown>>(
  tableKey: string,
  recordId: number,
  opts?: { anonymous?: boolean },
): Promise<T | null> {
  void opts;
  try {
    return await apiFetch<T>(`${tablePath(tableKey)}/${recordId}`);
  } catch (e) {
    if (e instanceof Error && e.message.includes('404')) return null;
    throw e;
  }
}

export async function deleteRecord(
  tableKey: string,
  recordId: number,
  _opts?: unknown,
): Promise<void> {
  await apiFetch<void>(`${tablePath(tableKey)}/${recordId}`, { method: 'DELETE' });
}

export async function bulkCreate<T = Record<string, unknown>>(
  tableKey: string,
  records: Record<string, unknown>[],
): Promise<T[]> {
  return apiFetch<T[]>(`${tablePath(tableKey)}/bulk`, {
    method: 'POST',
    body: JSON.stringify({ records }),
  });
}

export async function bulkDelete(
  tableKey: string,
  ids: number[],
): Promise<void> {
  await apiFetch<void>(`${tablePath(tableKey)}/bulk`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
}
